// #143: Filter des Katalog-Prozessbaums. Teilprojekt/Prozess wirken strukturell auf den Baum,
// Tester/Datei/Übersetzungslücke auf die Testfälle. Alles clientseitig wie die Suche (#78); der
// Zustand liegt in der URL (Muster #36/#84).
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CatalogTree, TestCaseListItem } from './catalog-api.js';

export interface CatalogFilter {
  teilprojekt: string;
  process: string;
  /** user_id, 'none' = ohne Standard-Tester, '' = kein Filter */
  tester: string;
  /** import_job_id, 'none' = ohne Import, '' = kein Filter */
  file: string;
  gap: boolean;
}

export const EMPTY_FILTER: CatalogFilter = {
  teilprojekt: '',
  process: '',
  tester: '',
  file: '',
  gap: false,
};

/** Wirkt auf Testfälle (und braucht deshalb die volle Testfallliste). */
export const hasTestCaseFilter = (f: CatalogFilter) => f.tester !== '' || f.file !== '' || f.gap;
export const hasAnyFilter = (f: CatalogFilter) =>
  f.teilprojekt !== '' || f.process !== '' || hasTestCaseFilter(f);
/** Anzahl aktiver Filter — für den Zähler am Knopf („Filter (2)"). */
export const activeFilterCount = (f: CatalogFilter) =>
  [f.teilprojekt !== '', f.process !== '', f.tester !== '', f.file !== '', f.gap].filter(Boolean)
    .length;

export function matchesTestCaseFilter(tc: TestCaseListItem, f: CatalogFilter): boolean {
  if (
    f.tester === 'none'
      ? tc.default_tester_id != null
      : f.tester !== '' && tc.default_tester_id !== f.tester
  )
    return false;
  if (
    f.file === 'none'
      ? tc.source_import_job_id != null
      : f.file !== '' && tc.source_import_job_id !== f.file
  )
    return false;
  if (f.gap && !tc.translation_gap) return false;
  return true;
}

export function filterTree(tree: CatalogTree, f: CatalogFilter): CatalogTree {
  const keepProcess = (p: { process_id: string }) => f.process === '' || p.process_id === f.process;
  return {
    ...tree,
    nodes: tree.nodes
      .filter((n) => f.teilprojekt === '' || n.node_id === f.teilprojekt)
      .map((n) => ({ ...n, processes: n.processes.filter(keepProcess) }))
      // Leere Teilprojekte räumt nur der Prozess-Filter weg — sonst bliebe der Baum ohne Filter
      // nicht so, wie er heute aussieht (leere Gruppen zeigt er an).
      .filter((n) => f.process === '' || n.processes.length > 0),
    unlabeled: f.teilprojekt === '' ? tree.unlabeled.filter(keepProcess) : [],
  };
}

type Option = { value: string; label: string };
function options(entries: Array<[string | null, string | null]>, noneLabel: string): Option[] {
  const map = new Map<string, string>();
  let hasNone = false;
  for (const [id, label] of entries) {
    if (id == null) hasNone = true;
    else map.set(id, label ?? id);
  }
  const out = [...map]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  return hasNone ? [...out, { value: 'none', label: noneLabel }] : out;
}
export const testerOptions = (tcs: TestCaseListItem[], noneLabel = '—') =>
  options(
    tcs.map((t) => [t.default_tester_id, t.default_tester_name]),
    noneLabel,
  );
export const fileOptions = (tcs: TestCaseListItem[], noneLabel = '—') =>
  options(
    tcs.map((t) => [t.source_import_job_id, t.source_filename]),
    noneLabel,
  );

const KEYS = {
  teilprojekt: 'f_tp',
  process: 'f_proc',
  tester: 'f_tester',
  file: 'f_file',
  gap: 'f_gap',
} as const;

export function useCatalogFilter(): [
  CatalogFilter,
  (patch: Partial<CatalogFilter>) => void,
  () => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const get = (k: string) => searchParams.get(k) ?? '';
  const teilprojekt = get(KEYS.teilprojekt);
  const process = get(KEYS.process);
  const tester = get(KEYS.tester);
  const file = get(KEYS.file);
  const gap = get(KEYS.gap) === '1';
  // Stabile Identität: nur ein geänderter Wert erzeugt ein neues Objekt — so taugt `filter` als
  // Abhängigkeit von useMemo im Prozessbaum.
  const filter = useMemo<CatalogFilter>(
    () => ({ teilprojekt, process, tester, file, gap }),
    [teilprojekt, process, tester, file, gap],
  );
  // EIN Schreibvorgang für alle Keys — mehrere useUrlState-Setter im selben Tick überschreiben
  // sich gegenseitig (prev = Stand des letzten Renders).
  const update = (patch: Partial<CatalogFilter>) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [field, key] of Object.entries(KEYS) as [keyof CatalogFilter, string][]) {
          if (!(field in patch)) continue;
          const v = patch[field];
          const s = typeof v === 'boolean' ? (v ? '1' : '') : (v ?? '');
          if (s === '') p.delete(key);
          else p.set(key, s);
        }
        return p;
      },
      { replace: true },
    );
  const reset = () => update(EMPTY_FILTER);
  return [filter, update, reset];
}
